import { useMemo } from 'react';
import { MoldOrder } from '../types/mold';
import { Section } from '../types/form';
import { calculateFormProgress, emptyProgressData, ProgressData, getValidationMessagesFromProgress } from '../utils/formProgress';

/**
 * Hook to calculate form progress and NG count from formData
 * Can be reused across components that need form progress information
 */
interface UseFormProgressProps {
	formData: Record<string, any>;
	sections: Section[];
	moldOrder: MoldOrder;
	loadedFormConfig?: { sections: Section[]; images?: any[]; visibleSteps?: any[] };
	currentFiles?: Array<{ tag?: string; tags?: string[] }>;
}

interface FormProgressResult extends ProgressData {
	ngCount: number;
}

export const useFormProgress = ({
	formData,
	sections,
	moldOrder,
	loadedFormConfig,
	currentFiles
}: UseFormProgressProps): FormProgressResult & { validationMessages?: string[] } => {
	// Memoize progress calculation to avoid recalculating on every render.
	// calculateFormProgress already derives ngCount from the same resolver-filtered
	// visible set, so there is no separate NG traversal here. moldOrder.status is a
	// defensive dep: calculateFormProgress runs the resolver without a
	// questionFilterStrategy today, so status does not affect this result — it is kept
	// so ngCount stays correct if a status-gated filter is ever wired into this path.
	const progress = useMemo(() => {
		if (!formData || sections.length === 0 || !moldOrder) {
			return emptyProgressData();
		}
		return calculateFormProgress(formData, sections, moldOrder, currentFiles);
	}, [formData, sections, moldOrder?.id, moldOrder?.type, moldOrder?.status, currentFiles]);

	const baseResult: FormProgressResult = {
		...progress,
		ngCount: progress.ngCount || 0
	};

	// Memoize validation messages when config/files provided
	const validationMessages = useMemo(() => {
		if (loadedFormConfig && currentFiles) {
			return getValidationMessagesFromProgress(baseResult, loadedFormConfig, currentFiles, moldOrder);
		}
		return undefined;
		// Depend on the memoized `progress` object directly rather than enumerating its
		// primitive fields: getValidationMessagesFromProgress reads unansweredRequiredQuestions
		// (not unansweredQuestions), so listing fields by hand risked a stale message count.
	}, [progress, loadedFormConfig, currentFiles, moldOrder?.id]);

	return {
		...baseResult,
		validationMessages
	};
};

/**
 * Hook variant that extracts formData from moldOrder
 * Useful when you have moldOrder but need to extract form data
 */
interface UseFormProgressFromMoldOrderProps {
	moldOrder: MoldOrder;
	sections: Section[];
	loadedFormConfig?: { sections: Section[]; images?: any[]; visibleSteps?: any[] };
	currentFiles?: Array<{ tag?: string; tags?: string[] }>;
}

export const useFormProgressFromMoldOrder = ({
	moldOrder,
	sections,
	loadedFormConfig,
	currentFiles
}: UseFormProgressFromMoldOrderProps): FormProgressResult & { validationMessages?: string[] } => {
	const formData = useMemo(() => {
		return moldOrder?.orderDetail?.form || {};
	}, [moldOrder?.orderDetail?.form]);

	return useFormProgress({ formData, sections, moldOrder, loadedFormConfig, currentFiles });
};

export type { FormProgressResult };
